import type { Property } from 'csstype';
import type { FC, PropsWithChildren } from 'react';
import { useEffect } from 'react';
import { useMap } from 'react-map-gl/mapbox';
import type { Bounds } from '../../utils/geo.js';
import { MAP_PADDING, MapView } from './view.js';
import { withMap } from './with-map.js';

interface Props {
  bounds?: Bounds;
  height?:
    | Property.Height<string | number>
    | NonNullable<Property.Height<string | number> | undefined>[]
    | undefined;
  mapStyle: string;
  minHeight?:
    | Property.Height<string | number>
    | NonNullable<Property.Height<string | number> | undefined>[]
    | undefined;
  minWidth?:
    | Property.Width<string | number>
    | NonNullable<Property.Width<string | number> | undefined>[]
    | undefined;
  width?:
    | Property.Width<string | number>
    | NonNullable<Property.Width<string | number> | undefined>[]
    | undefined;
}

const controller =
  (View: typeof MapView): FC<PropsWithChildren<Props>> =>
  (props) => {
    const { default: map } = useMap();

    useEffect(() => {
      if (!map) return;

      const resize = (): void => {
        map.resize();
      };

      map.on('idle', resize);

      return () => {
        map.off('idle', resize);
      };
    }, [map]);

    useEffect(() => {
      if (!map || !props.bounds) return;

      map.fitBounds(props.bounds, {
        padding: {
          bottom: MAP_PADDING,
          left: MAP_PADDING,
          right: MAP_PADDING,
          top: MAP_PADDING,
        },
      });
    }, [map, props.bounds]);

    return View({
      ...props,
      bearing: 0,
    });
  };

export default withMap(controller(MapView));
